import React, { HTMLAttributes, useRef, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";

// import required modules
import { twMerge } from "tailwind-merge";
import {
  PARKING_NAME_EN_MAP,
  REGION_DISTRICT_MAP,
  REGION_LIST,
  STATION_INFO_MAP,
} from ".";
import type { IParkingBeesEvChargerVacancyResponse } from "../../../apis/parkingbeesRelatedService/type";
import useEvInfoPolling from "../../../hooks/useEvInfoPolling";

interface IEvInfoSwiperMobile {
  goNextSection: () => void;
}

type TEvInfoSwiperMobileProps = IEvInfoSwiperMobile &
  HTMLAttributes<HTMLDivElement>;

const EvInfoSwiperMobile: React.FC<TEvInfoSwiperMobileProps> = (props) => {
  const { goNextSection, ...otherProps } = props;
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const loadingMap: Record<string, boolean> = {};
  const [showPricing, setShowPricing] = useState(false);
  const { evInfoList, isInited } = useEvInfoPolling();
  const listRef = useRef<HTMLDivElement>(null);

  function filterByRegion(
    list: IParkingBeesEvChargerVacancyResponse[] = [],
    regionKey: string,
  ) {
    if (!Array.isArray(list)) return [];
    if (regionKey === "all") return list;
    const districtIds = REGION_DISTRICT_MAP[regionKey] || [];
    return list.filter((item) =>
      districtIds.includes(item.parking.district_id),
    );
  }

  const filteredEvInfoList = Array.isArray(evInfoList)
    ? filterByRegion(evInfoList, selectedRegion) || []
    : [];

  return (
    <div className="flex laptop:hidden relative w-full max-h-[70vh]">
      {/* info section */}
      <div
        className={twMerge(
          "flex flex-col w-full gap-10 mx-0 p-5 rounded-xl bg-mainDark text-white transition-all duration-500",
          showPricing ? "opacity-50" : "opacity-100",
        )}
      >
        <div className="flex flex-row justify-between text-center">
          {REGION_LIST.map((region) => (
            <div
              key={region.key}
              className={twMerge(
                "flex flex-col",
                selectedRegion === region.key
                  ? "text-white font-bold border-b-2 border-white pb-2"
                  : "text-gray-400 cursor-pointer",
              )}
              onClick={() => {
                setSelectedRegion(region.key);
                setSelectedIndex(0);
                // scroll to top of the list
                listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <span className="font-bold">{region.name}</span>
              <span className="text-[10px]">{region.eng_name}</span>
            </div>
          ))}
        </div>
        <div
          className="flex flex-col gap-4 max-h-[400px] overflow-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          ref={listRef}
        >
          {!isInited &&
            Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={idx}
                className="flex flex-row gap-4 mx-0 p-5 rounded-xl bg-[#444] animate-pulse w-full min-h-[270px]"
              >
                <div className="flex flex-1 flex-col gap-4">
                  <div className="h-8 bg-[#666] rounded-xl mb-2" />
                  <div className="w-[92px] h-[92px] rounded-full bg-[#666]" />
                  <div className="flex flex-row gap-2">
                    <div className="w-8 h-8 bg-[#666] rounded" />
                    <div className="w-8 h-8 bg-[#666] rounded" />
                  </div>
                </div>
                <div className="flex flex-[3] flex-col gap-5">
                  <div className="h-6 bg-[#666] rounded mb-2" />
                  <div className="h-10 bg-[#666] rounded mb-2" />
                  <div className="h-6 bg-[#666] rounded mb-2" />
                </div>
              </div>
            ))}
          {isInited &&
            filteredEvInfoList.map(
              (evInfo: IParkingBeesEvChargerVacancyResponse, index: number) => {
                return (
                  <div
                    key={evInfo.ms_token}
                    className={twMerge(
                      "flex flex-row gap-4 mx-0 p-5 rounded-xl bg-[#666666] text-white w-full min-h-[270px] cursor-pointer",
                      selectedIndex === index ? "opacity-100" : "opacity-20",
                    )}
                    onClick={() => setSelectedIndex(index)}
                  >
                    {/* left */}
                    <div className="flex flex-1 flex-col gap-4">
                      <div className="font-bold text-sm rounded-xl bg-secondaryDark text-center py-1 px-2">
                        {evInfo.parking.name}
                        <br />
                        {PARKING_NAME_EN_MAP[evInfo.parking.name]}
                      </div>
                      <img
                        src={
                          evInfo.parking.id === 2
                            ? "https://parkingbees-global-images.s3.ap-southeast-1.amazonaws.com/khora_ev_cover.jpeg"
                            : evInfo.parking.cover
                        }
                        alt="address-image"
                        className="flex min-w-[92px] min-h-[92px] aspect-square rounded-full object-contain bg-black"
                      />
                      <div className="flex flex-row justify-around items-center">
                        <img
                          src={require("../../../assets/icons/free_parking_1hr.png")}
                          width={30}
                          height={70}
                          alt="free_parking_1hr"
                        />

                        <a
                          href={evInfo.parking.map}
                          target="_blank"
                          referrerPolicy="no-referrer"
                          rel="noreferrer"
                        >
                          <img
                            src={require("../../../assets/icons/locationArrow.png")}
                            width={30}
                            height={70}
                            alt="location"
                          />
                        </a>
                      </div>
                    </div>
                    {/* right */}
                    <div className="flex flex-[3] flex-col gap-5">
                      <div className="flex flex-col gap-2 border-b-2 border-secondaryDark pb-2">
                        <div>{evInfo.parking.display_name}</div>
                        <div>
                          {STATION_INFO_MAP[evInfo.parking.id]?.name_eng}
                        </div>
                      </div>

                      {evInfo.totalCount === 0 ? (
                        <div className="text-4xl text-mainYellow text-center mt-16">
                          敬請期待
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-row justify-between items-end">
                            <div className="text-sm">
                              閒置中
                              <br />
                              Available
                            </div>
                            <div className="flex flex-row items-end text-secondaryDark text-4xl font-bold">
                              <span className="text-mainLightBlue text-6xl">
                                {loadingMap[index] ? (
                                  <AiOutlineLoading className="text-6xl animate-spin" />
                                ) : (
                                  <>{evInfo.availableCount}</>
                                )}
                              </span>
                              /<span>{evInfo.totalCount}</span>
                            </div>
                          </div>
                          <div className="flex flex-row justify-between items-center">
                            <div className="text-sm w-24">
                              預計等待時間
                              <br />
                              Estimated Waiting Time
                            </div>
                            <div className="text-mainLightBlue text-lg font-bold">
                              {loadingMap[index] ? (
                                <AiOutlineLoading className="text-4xl animate-spin" />
                              ) : (
                                <>~{evInfo.estimatedWaitingTime} 分鐘</>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              },
            )}

          {isInited && filteredEvInfoList.length === 0 && (
            <div className="text-center text-2xl text-gray-400 h-[400px] mt-[50%]">
              即將推出
              <br />
              Coming soon
            </div>
          )}
        </div>

        <div
          className="flex flex-row gap-4 items-center text-center font-bold cursor-pointer ml-auto"
          onClick={() => {
            if (filteredEvInfoList.length === 0) return;
            setShowPricing(true);
          }}
        >
          <div>
            價目表
            <br />
            Price list
          </div>
          <LuArrowRight size={28} />
        </div>
      </div>

      {/* pricing section */}
      {filteredEvInfoList.length > 0 && (
        <div
          className={twMerge(
            "flex flex-col gap-8 py-8 rounded-xl bg-mainDark text-white absolute top-0 left-20 w-full h-full z-50 transition-transform duration-500 max-h-[70vh]",
            showPricing ? "-translate-x-20 " : "translate-x-full",
          )}
          style={{ boxShadow: showPricing ? "0 0 20px #0008" : "none" }}
        >
          <div className="flex flex-row mx-5 justify-center items-center gap-5 border-b border-white pb-8">
            <div className="flex-1 font-bold text-sm rounded-xl bg-secondaryDark text-center py-1 px-2">
              {filteredEvInfoList[selectedIndex].parking.name}
              <br />
              {
                PARKING_NAME_EN_MAP[
                  filteredEvInfoList[selectedIndex].parking.name
                ]
              }
            </div>
            <div className="flex flex-[2] flex-col gap-2">
              <div>
                {filteredEvInfoList[selectedIndex].parking.display_name}
              </div>
              <div>
                {STATION_INFO_MAP[filteredEvInfoList[selectedIndex]?.parking.id]
                  ?.name_eng ?? "-"}
              </div>
            </div>
          </div>
          <div className="flex flex-col px-10 gap-8">
            <img
              width={160}
              className="px-2"
              src={require("../../../assets/images/standard_fees.png")}
              alt="Standard Fees"
            />

            <div className="flex px-2 flex-row justify-between items-center">
              <div className="font-semibold text-xl w-[70px] text-secondaryDark">
                充電價格
                <br />
                Charging Fee
              </div>
              <div className="flex flex-col gap-2 text-right font-semibold">
                <span className="text-6xl text-mainYellow">
                  $
                  {filteredEvInfoList[selectedIndex]?.priceMin ??
                    STATION_INFO_MAP[
                      filteredEvInfoList[selectedIndex]?.parking.id
                    ]?.price}
                </span>
                <span className="text-4xl text-secondaryDark"> / kWh</span>
                <span className="text-xl">
                  {STATION_INFO_MAP[
                    filteredEvInfoList[selectedIndex]?.parking.id
                  ]?.time_period ?? "-"}
                </span>
              </div>
            </div>

            <div className="flex px-2 relative">
              <img
                src={require("../../../assets/images/bubble_desktop.png")}
                alt="bubble_d"
                className="w-full h-auto"
              />
              <div className="absolute flex flex-row text-white left-[30%] top-[18px] text-center items-baseline">
                <span className="text-base">Up to</span>
                <span className="text-2xl font-semibold ml-2">
                  {STATION_INFO_MAP[
                    filteredEvInfoList[selectedIndex]?.parking.id
                  ]?.kwh ?? "-"}{" "}
                  kW
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex mx-5 items-center flex-row gap-4 text-left mt-auto font-bold cursor-pointer"
            onClick={() => setShowPricing(false)}
          >
            <LuArrowLeft size={28} />
            <div>
              返回
              <br />
              Back
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvInfoSwiperMobile;
